<template>
  <div class="body-wrapper">
    <Header :title="'Ads Manager'" />
    
    <div class="stat-options">
      <StatOptions
        :selectedRange="selectedRange"
        @changeDate="changeDate" />
      <button class="secondary-btn-dark" @click="createCampaign = true;">NEW CAMPAIGN</button>
    </div>

    <div class="ads-wizard">
      <div class="step step-1">
        <div class="campaign" 
          v-for="campaign in campaigns" 
          @click="selectCampaign(campaign)" 
          :key="campaign.id" 
          :class="{'active': selectedCampaign(campaign.id)}">
          <div class="details">
            <div class="top-row">
              <span>{{ campaign.owner === 'Wotive' ? 'INTERNAL WOTIVE CAMPAIGN' : 'PARTNER CAMPAIGN' }}</span>
              <span class="ad-status-holder">{{ activeAds(campaign) ? 'ACTIVE ADS' : 'NO ACTIVE ADS' }} <div class="status" :class="{ 'active-ads': activeAds(campaign) }"></div></span>
            </div>
            <h2>{{ campaign.title }}</h2>
            <span>ALL LOCATIONS</span>
          </div>
          <div class="stats">
            <div class="stat-row">
              <span>VIEWS:</span>
              <span>{{ totalViews(campaign) }}</span>
            </div>
            <div class="stat-row">
              <span>CLICKS/CTR%:</span>
              <span>{{ totalClicks(campaign) || 0 }}/{{ totalClicks(campaign)/totalViews(campaign) || 0 }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="step step-2" v-if="currentCampaign.id">
        <div class="campaign-header">
          <div class="top-row">
            <h2>{{ currentCampaign.title }}</h2>
            <i class="fas fa-cog" @click="editCampaign()"></i>
            <button class="secondary-btn-dark" @click="newAdvertClicked()">NEW AD</button>
          </div>
        </div>
        <div class="campaign-body">
          <div class="campaign-stats">
            <span>{{ adverts.length }} Ads</span>
            <span>Selected Date Range: {{ selectedRange }}</span>
          </div>
          <div 
            v-for="advert in adverts" 
            class="campaign" 
            :key="advert.id" 
            @click="selectAdvert(advert)"
            :class="{'active': selectedAdvert(advert.id)}">
            <div class="details">
              <div class="left-details">
                <div class="ad-image">
                  <img class="display-img" v-if="advert.image !== ''" id="preview" :src="url + advert.image" />
                  <img class="default-img" v-if="advert.image === ''" id="preview" src="../assets/add_photo_grey.png" />
                </div>
                <h4>{{ advert.title }}</h4>
              </div>
              <div class="right-details">
                <span>ACTIVE</span>
                <ToggleButton @change="activateAdvert(advert)" :value="advert.active" />
              </div>
            </div>
            <div class="stats">
              <div class="stat-row">
                <span>VIEWS:</span>
                <span>{{ advert.views }}</span>
              </div>
              <div class="stat-row">
                <span>CLICKS/CTR%:</span>
                <span>{{ advert.clicks || 0 }}/{{ advert.clicks / advert.views || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="step step-3" v-if="currentAdvert">
        <div class="campaign-header">
          <div class="campaign-preview-pic">
            <img class="display-img" v-if="previewUrl" id="preview" :src="previewUrl" />
            <img class="display-img" v-else-if="currentAdvert.image !== ''" id="preview" :src="url + currentAdvert.image" />
            <img class="default-img" v-else id="preview" src="../assets/add_photo_grey.png" />
          </div>
          <div class="campaign-preview-footer">
            <p>{{ advertDescription }}</p>
            <button class="secondary-btn-dark">{{ advertButtonText.toUpperCase() }}</button>
          </div>
        </div>
        <div class="campaign-body">
          <div class="attribute">
            <span>Ad Title</span>
            <input type="text" v-model="advertTitle" />
          </div>
          <div class="picture-holder">
            <div class="picture-box">
              <form enctype="multipart/form-data" novalidate>
                <div class="dropbox default">
                  <input
                    type="file"
                    @change="
                      filesChange($event.target.files);
                      fileCount = $event.target.files.length;
                    "
                    accept="image/*"
                    class="input-file"
                  />
                  <img class="default-img" id="preview" src="../assets/add_photo_grey.png" />
                </div>
              </form>
            </div>
            <p>
              Image Ratio is 2:1<br>
              Image Pixel Size: 800x400
            </p>
          </div>
          <div class="attribute">
            <span>Ad Text</span>
            <input type="text" v-model="advertDescription" />
          </div>
          <div class="attribute-row">
            <div class="attribute">
              <span>Button Text</span>
              <select v-model="advertButtonText">
                <option value="Learn More">LEARN MORE</option>
                <option value="Upgrade">UPGRADE</option>
                <option value="Shop Now">SHOP NOW</option>
              </select>
            </div>
            <div class="attribute">
              <span>Button Destination</span>
              <select v-model="advertButtonDestination">
                <option value="INTERNAL">Internal Link</option>
                <option value="EXTERNAL">External Link</option>
              </select>
            </div>
          </div>
          <div v-if="advertButtonDestination === 'INTERNAL'">
            <div class="attribute">
              <span>Internal Page</span>
              <select v-model="advertLink">
                <option value="/custom">Custom workout</option>
                <option value="/create_exercise">Create exercise</option>
                <option value="/explore">Explore</option>
                <option value="/explore/challenges">Challenges</option>
                <option value="/find_friends">Find friends</option>
                <option value="/invite_friends">Invite friends</option>
                <option value="/feedback">Feedback</option>
                <option value="/achievements">Own achievements</option>
                <option value="/progression">Own progression</option>
                <option value="/premade_workouts">Premade workouts</option>
                <option value="/membership">Pro membership page</option>
              </select>
            </div>
          </div>
          <div v-else>
            <div class="attribute">
              <span>External Link</span>
              <input placeholder="https://example.com" type="text" v-model="advertLink" />
            </div>
          </div>
          <div class="advert-actions">
            <a @click="deleteAdvertClicked()"><i class="fas fa-trash"></i></a>
            <button class="primary-btn" @click="saveAdvert()">SAVE AD</button>
          </div>
        </div>
      </div>
    </div>

    <CampaignEditModal v-if="createCampaign" />
  </div>
</template>

<script>
import { updateAdvert, createAdvert, deleteAdvert } from '@/api/requests';
import { ToggleButton } from 'vue-js-toggle-button'
import Header from "@/components/header/Header";
import StatOptions from "@/components/stats/StatOptions";
import CampaignEditModal from "@/components/modals/CampaignEditModal";

export default {
  name: 'Promotional',

  data () {
    return {
      selectedRange: 'TD',
      previewUrl: '',
      advertTitle: '',
      advertDescription: '',
      advertButtonText: '',
      advertButtonDestination: 'EXTERNAL',
      advertLink: '',
      fileToUpload: null,
    };
  },

  components: {
    Header,
    ToggleButton,
    StatOptions,
    CampaignEditModal
  },

  mounted() {
    this.$store.dispatch('campaigns/loadCampaigns', { date: this.selectedRange });
  },

  methods: {
    selectCampaign(campaign) {
      this.$store.dispatch('campaigns/campaignClicked', campaign);
    },

    selectAdvert(advert) {
      this.$store.dispatch('campaigns/advertClicked', advert);
      this.advertTitle = advert.title;
      this.advertDescription = advert.description;
      this.advertButtonText = advert.button_text;
      this.advertButtonDestination = advert.destination;
      this.advertLink = advert.destination_value;
      this.fileToUpload = null;
      this.previewUrl = '';
    },

    selectedCampaign(id) {
      return this.$store.state.campaigns.selectedCampaign?.id === id;
    },

    selectedAdvert(id) {
      return this.$store.state.campaigns.selectedAdvert?.id === id;
    },

    newAdvertClicked() {
      this.$store.dispatch('campaigns/newAdvertClicked');
      this.advertTitle = '';
      this.advertDescription = '';
      this.advertButtonText = 'Learn More';
      this.advertButtonDestination = 'EXTERNAL';
      this.advertLink = '';
      this.previewUrl = '';
      this.fileToUpload = null;
    },

    saveAdvert() {
      let payload = {
        title: this.advertTitle,
        description: this.advertDescription,
        button_text: this.advertButtonText,
        destination: this.advertButtonDestination,
        destination_value: this.advertLink,
        campaign_id: this.currentCampaign.id
      };

      let formData = new FormData();
      for (var key in payload) {
        formData.append(key, payload[key]);
      }
      if (this.fileToUpload) {
        formData.append('image', this.fileToUpload, this.fileToUpload.name);
      }

      if (this.currentAdvert.id) {
        // ID exists, we are editing
        updateAdvert(this.currentAdvert.id, formData).then((response) => {
          if (response.data.image) {
            payload['image'] = response.data.image;
          }
          this.$store.commit('campaigns/updateAdvert', payload);
        });
      } else {
        // No ID, we are creating
        createAdvert(formData).then((response) => {
          payload['id'] = response.data.id;
          payload['image'] = response.data.image;
          this.$store.commit('campaigns/addAdvert', payload);
          this.$toasted.global.app_success({
            message: "Advert has been created."
          });
        });
      }
    },

    deleteAdvertClicked() {
      this.$confirm(
        "Are you sure you want to delete this advert?", 
        "DELETE ADVERT").then(() => {
          if (this.currentAdvert.id) {
            deleteAdvert(this.currentAdvert.id).then(() => {
              this.$store.commit('campaigns/removeAdvert', this.currentAdvert.id);
            });
          } else {
            this.$store.dispatch('campaigns/closeAdvertDetails');
          }
        }).catch(() => {
          this.$toasted.global.app_error({
            message: "Failed to delete advert."
          });
        });
    },

    activateAdvert(advert) {
      let payload = {
        active: !advert.active
      };
      let formData = new FormData();
      for (var key in payload) {
        formData.append(key, payload[key]);
      }
      updateAdvert(advert.id, formData).then(() => {
        this.$store.commit('campaigns/updateAdvert', payload);
      });
    },

    activeAds(campaign) {
      const adverts = campaign.adverts || [];
      let activeAds = false;
      
      adverts.forEach(ad => {
        if (ad.active) {
          activeAds = true;
        }
      });

      return activeAds;
    },

    filesChange(fileList) {
      this.previewUrl = URL.createObjectURL(fileList[0]);
      this.fileToUpload = fileList[0];
    },

    changeDate(value) {
      this.selectedRange = value;
      this.$store.dispatch('campaigns/loadCampaigns', { date: this.selectedRange });
    },

    totalViews(campaign) {
      let views = 0;
      if (campaign.adverts) {
        campaign.adverts.forEach(ad => {
          views += ad.views;
        });
      }

      return views;
    },

    totalClicks(campaign) {
      let clicks = 0;
      if (campaign.adverts) {
        campaign.adverts.forEach(ad => {
          clicks += ad.clicks;
        });
      }

      return clicks;
    },

    editCampaign() {
      this.$store.dispatch('campaigns/editCampaignClicked', this.currentCampaign);
    }
  },

  computed: {
    campaigns() {
      return this.$store.state.campaigns.campaigns;
    },

    currentCampaign() {
      return this.$store.state.campaigns.selectedCampaign;
    },

    adverts() {
      return this.$store.state.campaigns.adverts;
    },

    currentAdvert() {
      return this.$store.state.campaigns.selectedAdvert;
    },

    url() {
      let url = window.location.origin;
      if (url === "http://localhost:8080") {
        url = "http://localhost:3000";
      }
      return url;
    },

    createCampaign: {
      get () {
        return this.$store.state.campaigns.createCampaign
      },
      set (value) {
        this.$store.commit('campaigns/updateAttribute', {value: value, type: 'createCampaign'})
      }
    },
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables";
$secondary-grey: #333546;

.stat-options {
  display: flex;
  padding: $spacing-base;
  padding-bottom: 0;

  button {
    height: 25px;
    margin-left: $spacing-base;
  }
}

.ads-wizard {
  display: flex;
  flex-direction: row;
  padding: $spacing-base;

  .step {
    gap: 10px;
  }

  .step-1 {
    width: 30%;
    margin-right: $spacing-base;
  }

  .step-2 {
    width: 35%;
    margin-right: $spacing-base;

    .campaign-header {
      height: 62px;
      background: $secondary-grey;

      .top-row {
        display: flex;
        flex-direction: row;
        padding: $spacing-base;
        align-items: center;

        i {
          color: $sec5;
          margin-right: $spacing-small;
          cursor: pointer;
        }
  
        h2 {
          flex: 1;
          padding: 0;
          margin: 0;
          font-size: $font-size-medium;
          color: white;
          font-weight: bold;
        }

        button {
          height: 30px;
        }

      }
    }

    .campaign-body {
      padding: $spacing-base;
      background: $sec0;

      .campaign-stats {
        display: flex;
        justify-content: space-between;
        font-size: $font-size-small;
        color: $sec1;
        margin-bottom: $spacing-base;
      }

      .campaign {
        height: 70px;

        .details {
          flex: 6;
          background: #323344;
          display: flex;
          padding: 0;
          flex-direction: row;
          justify-content: space-between;

          .left-details {
            display: flex;
            align-items: center;
            
            .ad-image {
              width: 143px;
              height: 70px;
              justify-content: center;
              align-items: center;
              display: flex;

              .display-img {
                width: 100%;
                height: 100%;
              }
            }
            h4 {
              color: white;
              font-size: $font-size-base;
              font-weight: 500;
              margin: 0;
              margin-left: $spacing-base/2;
              padding: 0;
              vertical-align: center;
            }
          }

          .right-details {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding-right: $spacing-base/2;

            span {
              margin-bottom: $spacing-base/2;
            }
          }
        }

        .stats {
          background: #3D3E53;
          padding: 12px;
        }
      }

      .active {
        .details {
          background: $blue;

          .top-row {
            span {
              color: white;
            }
          }
          span {
            color: white;
          }
        }

        .stats {
          background: #0D579B;

          .stat-row {
            color: white;
          }
        }
      }
    }
  }

  .step-3 {
    width: 395px;
    background: $sec0;
    
    .campaign-header {
      height: 240px;
      background: $secondary-grey;
      padding: $spacing-base;

      .campaign-preview-pic {
        background: $sec2;
        justify-content: center;
        align-items: center;
        display: flex;
        width: 360px;
        height: 180px;

        img {
          width: 100%;
        }
      }

      .campaign-preview-footer {
        display: flex;

        p {
          flex: 1;
          font-size: $font-size-small;
          color: $sec7;
          margin-right: 40px;
        }
        
        button {
          margin-top: $spacing-base/2;
          height: 30px;
          font-size: $font-size-small;
          width: auto;
        }
      }
    }

    .campaign-body {
      padding: $spacing-base;

      .picture-holder {
        display: flex;
        margin-bottom: $spacing-base;

        .picture-box {
          width: 160px;
          height: 80px;
        }

        .dropbox { 
          width: 100%;
          background: $sec2;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 0;

          .default-img {
            width: 20px;
            height: 20px;
          }

          .display-img {
            width: 100%;
            border-radius: 0;
          }
        }

        p {
          flex: 3;
          margin: 0;
          margin-left: $spacing-base/2;
          padding: 0;
          font-size: $font-size-small;
          color: $sec2;
        }
      }

      .attribute {
        margin-bottom: $spacing-base;
        flex: 1;
        input {
          background: $dark;
          width: 100%;
          box-sizing: border-box;
        }
        select {
          width: 100%;
          background: $dark;
        }
        span {
          display: block;
          color: $sec7;
          font-size: $font-size-base;
          font-weight: bold;
          margin-bottom: $spacing-small / 2;
        }
      }

      .attribute-row {
        display: flex;
        gap: $spacing-base / 2;
      }

      .advert-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;

        i {
          cursor: pointer;
          color: $sec5;
        }
      }
    }
  }

  .campaign {
    width: 100%;
    height: 80px;
    display: flex;
    margin-bottom: $spacing-base;
    cursor: pointer;

    .details {
      background: $sec0;
      flex: 4;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: $spacing-base;

      .top-row {
        display: flex;
        justify-content: space-between;

        .ad-status-holder {
          display: flex;
          align-items: center;
        }

        .status {
          width: 8px;
          height: 8px;
          background: $sec1;
          border-radius: 10px;
          margin-left: 3px;
        }
        .active-ads {
          background: $green2;
        }
      }

      span {
        color: $sec4;
        font-size: $font-size-small;
      }

      h2 {
         padding: 0;
         margin: 0;
         font-size: $font-size-medium;
         color: white;
         font-weight: bold;
      }
    }

    .stats {
      background: $secondary-grey;
      flex: 2;
      padding: $spacing-base;

      .stat-row {
        display: flex;
        justify-content: space-between;
        font-size: $font-size-small;
        color: $sec5;
        margin-bottom: $spacing-small/2;
      }
    }
  }

  .active {
    .details {
      background: $blue;

      .top-row {
        span {
          color: white;
        }
      }
      span {
        color: white;
      }
    }

    .stats {
      background: #0D579B;

      .stat-row {
        color: white;
      }
    }
  }

}
</style>
